import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRecoveryClass, saveRecoveryGrade, saveRecoveryFinal } from '../../Api';
import LoadingSpinner from '../../components/Loading';

const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : String(Math.round(Number(n) * 100) / 100).replace('.', ','));
const num = (v) => {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};
// mesma regra do servidor (só para pré-visualizar)
const calcularFinal = (o, r, m, regra) => {
    if (r === null) return o;
    let f = Math.max(o, r);
    if (regra === 'media') f = Math.max(o, (o + r) / 2);
    if (regra === 'ate_media') f = Math.max(o, m !== null ? Math.min(r, m) : r);
    return Math.round(f * 100) / 100;
};
const REGRA_TXT = { maior: 'fica a maior nota', media: 'média entre as notas', ate_media: 'recuperação vale até a média' };

const box = {
    width: '100%', maxWidth: 900, boxSizing: 'border-box', margin: '0 auto',
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
    border: '1px solid var(--line, #e3e8eb)', borderRadius: 14, padding: '20px',
};

const Recovery = () => {
    const navigate = useNavigate();
    const { id_class } = useParams();
    const [loading, setLoading] = useState(true);
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState('');
    const [periodoIdx, setPeriodoIdx] = useState(0);
    const [valores, setValores] = useState({}); // chave aluno|materia -> texto digitado
    const [salvando, setSalvando] = useState('');
    const [aviso, setAviso] = useState(null);
    const [aba, setAba] = useState('periodo'); // 'periodo' | 'final'

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro('');
        try {
            const r = await getRecoveryClass(id_class);
            setDados(r.data);
            const ps = r.data?.periodos || [];
            setPeriodoIdx((i) => (i < ps.length ? i : Math.max(0, ps.length - 1)));
        } catch (e) {
            setErro(e?.response?.data?.msg || 'Não foi possível carregar a recuperação da turma.');
        } finally {
            setLoading(false);
        }
    }, [id_class]);

    useEffect(() => { carregar(); }, [carregar]);

    const periodo = dados?.periodos?.[periodoIdx] || null;
    const regra = dados?.config?.paralela?.regra || 'maior';
    const nomePeriodo = (p) => {
        const regime = (sessionStorage.getItem('assessmentRegime') || '').toUpperCase();
        return regime === 'TRIMESTRAL' ? String(p?.bimonthly || '').replace(/BIMESTRE/gi, 'TRIMESTRE') : p?.bimonthly;
    };

    const linhas = useMemo(() => {
        if (!periodo) return [];
        return periodo.alunos.flatMap((a) => a.materias.map((m) => ({ ...m, id_student: a.id_student, nome: a.nome })));
    }, [periodo]);

    // ── recuperação FINAL ──
    const fin = dados?.final?.ativa ? dados.final : null;
    const linhasFinal = useMemo(() => {
        if (!fin) return [];
        return (fin.alunos || []).flatMap((a) => a.materias.map((m) => ({ ...m, id_student: a.id_student, nome: a.nome })));
    }, [fin]);

    const salvarFinal = async (linha) => {
        const chave = `F|${linha.id_student}|${linha.materia}`;
        const texto = valores[chave] ?? (linha.recuperacao ?? '');
        setSalvando(chave);
        setAviso(null);
        try {
            await saveRecoveryFinal({
                id_class,
                id_student: linha.id_student,
                materia: linha.materia,
                nota: String(texto).trim() === '' ? null : String(texto).replace(',', '.'),
            });
            setAviso({ tipo: 'ok', texto: `Recuperação final de ${linha.nome} (${linha.materia}) salva.` });
            setValores((v) => { const n = { ...v }; delete n[chave]; return n; });
            await carregar();
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e?.response?.data?.msg || 'Erro ao salvar a recuperação final.' });
        } finally {
            setSalvando('');
        }
    };

    const salvar = async (linha) => {
        const chave = `${linha.id_student}|${linha.materia}`;
        const texto = valores[chave] ?? (linha.recuperacao ?? '');
        setSalvando(chave);
        setAviso(null);
        try {
            await saveRecoveryGrade({
                id_class,
                id_student: linha.id_student,
                quarterField: periodo.campo,
                id_quarter: periodo.id_quarter,
                materia: linha.materia,
                nota: String(texto).trim() === '' ? null : String(texto).replace(',', '.'),
            });
            setAviso({ tipo: 'ok', texto: `Recuperação de ${linha.nome} (${linha.materia}) salva.` });
            setValores((v) => { const n = { ...v }; delete n[chave]; return n; });
            await carregar();
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e?.response?.data?.msg || 'Erro ao salvar a recuperação.' });
        } finally {
            setSalvando('');
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div style={{ width: '100%', padding: 16, boxSizing: 'border-box' }}>
            <div style={box}>
                <h2 style={{ margin: '0 0 4px' }}>🔁 Recuperação</h2>
                <p style={{ margin: '0 0 14px', color: 'var(--text-muted, #5b6b72)', fontSize: 14 }}>
                    Alunos abaixo da média em cada período. Lance a nota da recuperação <strong>antes de fechar o diário</strong>: ao fechar, ela entra no boletim. A recuperação não conta nos pontos do período.
                    {dados?.ativa && <> Regra da escola: <strong>{REGRA_TXT[regra]}</strong>.</>}
                </p>

                {erro && <div style={{ color: 'var(--red, #cf3a3a)', marginBottom: 12 }}>{erro}</div>}

                {!erro && dados && dados.conceito && (
                    <div style={{ color: 'var(--text-muted, #5b6b72)' }}>
                        Esta escola avalia por conceitos: a recuperação por nota não se aplica.
                    </div>
                )}

                {!erro && dados && !dados.ativa && !dados.conceito && !fin && (
                    <div style={{ background: 'var(--orange-soft, #fff1e0)', border: '1px solid var(--orange, #e67e22)', borderRadius: 10, padding: '12px 14px' }}>
                        ⚠️ A escola ainda não configurou a recuperação. Peça à direção ou à secretaria para configurar
                        (Período Avaliativo → Configurar recuperação). Os alunos abaixo da média aparecerão aqui depois disso.
                    </div>
                )}

                {!erro && dados?.ativa && aba === 'periodo' && (dados.periodos || []).length === 0 && (
                    <div style={{ color: 'var(--text-muted, #5b6b72)' }}>Nenhum período com notas lançadas ainda.</div>
                )}

                {!erro && dados && !dados.conceito && (dados.ativa || fin) && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        {dados.ativa && (dados.periodos || []).map((p, i) => (
                            <button key={p.id_quarter} type="button" onClick={() => { setAba('periodo'); setPeriodoIdx(i); setAviso(null); }} style={{
                                padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                border: '2px solid var(--teal, #003e4f)',
                                background: aba === 'periodo' && i === periodoIdx ? 'var(--teal, #003e4f)' : 'transparent',
                                color: aba === 'periodo' && i === periodoIdx ? '#fff' : 'var(--teal-text, #003e4f)',
                            }}>
                                {nomePeriodo(p)} ({p.alunos.reduce((n, a) => n + a.materias.length, 0)})
                            </button>
                        ))}
                        {fin && (
                            <button type="button" onClick={() => { setAba('final'); setAviso(null); }} style={{
                                padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                border: '2px solid #0891b2',
                                background: aba === 'final' ? '#0891b2' : 'transparent',
                                color: aba === 'final' ? '#fff' : '#0e7490',
                            }}>
                                🏁 Recuperação final ({linhasFinal.length})
                            </button>
                        )}
                    </div>
                )}

                {!erro && dados?.ativa && aba === 'periodo' && (dados.periodos || []).length > 0 && (
                    <>

                        {periodo && (
                            <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)', marginBottom: 10 }}>
                                Total do período: <strong>{fmt(periodo.total)}</strong> · Média: <strong>{fmt(periodo.media)}</strong>
                            </div>
                        )}

                        {aviso && (
                            <div style={{
                                margin: '0 0 10px', padding: '9px 12px', borderRadius: 8, fontSize: 14,
                                background: aviso.tipo === 'ok' ? 'var(--green-soft, #e3f4ec)' : 'var(--red-soft, #fbe9e9)',
                                color: aviso.tipo === 'ok' ? 'var(--green, #1d8b5a)' : 'var(--red, #cf3a3a)',
                            }}>{aviso.texto}</div>
                        )}

                        {linhas.length === 0 ? (
                            <div style={{ color: 'var(--text-muted, #5b6b72)' }}>Nenhum aluno abaixo da média neste período. 🎉</div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--line, #e3e8eb)' }}>
                                            <th style={{ padding: 8 }}>Aluno</th>
                                            <th style={{ padding: 8 }}>Matéria</th>
                                            <th style={{ padding: 8 }}>Nota do período</th>
                                            <th style={{ padding: 8 }}>Nota da recuperação</th>
                                            <th style={{ padding: 8 }}>Nota final</th>
                                            <th style={{ padding: 8 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linhas.map((l) => {
                                            const chave = `${l.id_student}|${l.materia}`;
                                            const texto = valores[chave] ?? (l.recuperacao !== null ? String(l.recuperacao).replace('.', ',') : '');
                                            const previa = calcularFinal(l.original, num(texto), periodo.media, regra);
                                            const mudou = valores[chave] !== undefined;
                                            return (
                                                <tr key={chave} style={{ borderBottom: '1px solid var(--line, #e3e8eb)' }}>
                                                    <td style={{ padding: 8 }}>{l.nome}</td>
                                                    <td style={{ padding: 8 }}>{l.materia}</td>
                                                    <td style={{ padding: 8, color: 'var(--red, #cf3a3a)', fontWeight: 700 }}>{fmt(l.original)}</td>
                                                    <td style={{ padding: 8 }}>
                                                        {l.diarioAberto === false && (
                                                            <div style={{ fontSize: 11, color: 'var(--text-muted, #5b6b72)' }}>Diário fechado</div>
                                                        )}
                                                        <input
                                                            disabled={l.diarioAberto === false}
                                                            title={l.diarioAberto === false ? 'Reabra o diário do período para alterar a nota da recuperação' : undefined}
                                                            inputMode="decimal"
                                                            value={texto}
                                                            placeholder="—"
                                                            onChange={(e) => setValores((v) => ({ ...v, [chave]: e.target.value.replace(/[^\d,.]/g, '') }))}
                                                            style={{ width: 80, padding: '7px 9px', borderRadius: 8, border: '1px solid var(--line, #d0d7db)', background: 'var(--surface, #fff)', color: 'var(--text, #16323b)' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: 8, fontWeight: 800 }}>
                                                        {fmt(previa)}
                                                        {num(texto) !== null && <div style={{ fontSize: 11, fontWeight: 700, color: '#c026d3' }}>{fmt(l.original)} Rec= {fmt(previa)}</div>}
                                                    </td>
                                                    <td style={{ padding: 8 }}>
                                                        <button type="button" disabled={!mudou || salvando === chave} onClick={() => salvar(l)} style={{
                                                            padding: '7px 14px', borderRadius: 8, border: 'none', fontWeight: 700,
                                                            cursor: mudou ? 'pointer' : 'default',
                                                            background: mudou ? 'var(--teal, #003e4f)' : 'var(--line, #e3e8eb)',
                                                            color: mudou ? '#fff' : 'var(--text-muted, #5b6b72)',
                                                        }}>{salvando === chave ? 'Salvando…' : 'Salvar'}</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <p style={{ fontSize: 12, color: 'var(--text-muted, #5b6b72)' }}>
                                    Para remover uma recuperação, apague a nota e clique em Salvar.
                                </p>
                            </div>
                        )}
                    </>
                )}

                {!erro && fin && aba === 'final' && (
                    <>
                        <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)', marginBottom: 10 }}>
                            Alunos que estão fechando o ano abaixo da média anual. Total anual: <strong>{fmt(fin.totalAnual)}</strong> ·
                            Média anual: <strong>{fmt(fin.mediaAnual)}</strong> · Regra: <strong>{REGRA_TXT[fin.regra] || REGRA_TXT.ate_media}</strong>.
                            Lance a nota <strong>antes de fechar o diário do {nomePeriodo({ bimonthly: fin.ultimoPeriodo }) || 'último período'}</strong>.
                        </div>
                        {!fin.completo && (
                            <div style={{ background: 'var(--orange-soft, #fff1e0)', border: '1px solid var(--orange, #e67e22)', borderRadius: 10, padding: '10px 12px', marginBottom: 10, fontSize: 14 }}>
                                ⚠️ Nem todos os períodos do ano estão cadastrados. A recuperação final aparece quando todos estiverem cadastrados.
                            </div>
                        )}
                        {aviso && (
                            <div style={{
                                margin: '0 0 10px', padding: '9px 12px', borderRadius: 8, fontSize: 14,
                                background: aviso.tipo === 'ok' ? 'var(--green-soft, #e3f4ec)' : 'var(--red-soft, #fbe9e9)',
                                color: aviso.tipo === 'ok' ? 'var(--green, #1d8b5a)' : 'var(--red, #cf3a3a)',
                            }}>{aviso.texto}</div>
                        )}
                        {linhasFinal.length === 0 ? (
                            <div style={{ color: 'var(--text-muted, #5b6b72)' }}>Nenhum aluno abaixo da média anual. 🎉</div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--line, #e3e8eb)' }}>
                                            <th style={{ padding: 8 }}>Aluno</th>
                                            <th style={{ padding: 8 }}>Matéria</th>
                                            <th style={{ padding: 8 }}>Total do ano</th>
                                            <th style={{ padding: 8 }}>Nota da recuperação final</th>
                                            <th style={{ padding: 8 }}>Nota final do ano</th>
                                            <th style={{ padding: 8 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linhasFinal.map((l) => {
                                            const chave = `F|${l.id_student}|${l.materia}`;
                                            const texto = valores[chave] ?? (l.recuperacao !== null ? String(l.recuperacao).replace('.', ',') : '');
                                            const previa = calcularFinal(l.totalAno, num(texto), fin.mediaAnual, fin.regra);
                                            const mudou = valores[chave] !== undefined;
                                            return (
                                                <tr key={chave} style={{ borderBottom: '1px solid var(--line, #e3e8eb)' }}>
                                                    <td style={{ padding: 8 }}>{l.nome}</td>
                                                    <td style={{ padding: 8 }}>{l.materia}</td>
                                                    <td style={{ padding: 8, color: 'var(--red, #cf3a3a)', fontWeight: 700 }}>{fmt(l.totalAno)}</td>
                                                    <td style={{ padding: 8 }}>
                                                        {l.diarioAberto === false && (
                                                            <div style={{ fontSize: 11, color: 'var(--text-muted, #5b6b72)' }}>Diário do último período fechado</div>
                                                        )}
                                                        <input
                                                            disabled={l.diarioAberto === false}
                                                            title={l.diarioAberto === false ? 'Reabra o diário do último período para alterar a recuperação final' : undefined}
                                                            inputMode="decimal"
                                                            value={texto}
                                                            placeholder="—"
                                                            onChange={(e) => setValores((v) => ({ ...v, [chave]: e.target.value.replace(/[^\d,.]/g, '') }))}
                                                            style={{ width: 80, padding: '7px 9px', borderRadius: 8, border: '1px solid var(--line, #d0d7db)', background: 'var(--surface, #fff)', color: 'var(--text, #16323b)' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: 8, fontWeight: 800 }}>
                                                        {fmt(previa)}
                                                        {num(texto) !== null && <div style={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>{fmt(l.totalAno)} RecF= {fmt(previa)}</div>}
                                                    </td>
                                                    <td style={{ padding: 8 }}>
                                                        <button type="button" disabled={!mudou || salvando === chave} onClick={() => salvarFinal(l)} style={{
                                                            padding: '7px 14px', borderRadius: 8, border: 'none', fontWeight: 700,
                                                            cursor: mudou ? 'pointer' : 'default',
                                                            background: mudou ? '#0891b2' : 'var(--line, #e3e8eb)',
                                                            color: mudou ? '#fff' : 'var(--text-muted, #5b6b72)',
                                                        }}>{salvando === chave ? 'Salvando…' : 'Salvar'}</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <p style={{ fontSize: 12, color: 'var(--text-muted, #5b6b72)' }}>
                                    Para remover uma recuperação final, apague a nota e clique em Salvar.
                                </p>
                            </div>
                        )}
                    </>
                )}

                <div style={{ marginTop: 14 }}>
                    <button type="button" onClick={() => navigate(-1)} style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid var(--line, #d0d7db)', background: 'transparent', color: 'var(--text, #16323b)', fontWeight: 700, cursor: 'pointer' }}>
                        Voltar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Recovery;
